import { useState, useEffect, useRef } from "react";
import MembershipFDBG from "@/assets/MembershipFDBG.svg";
import BGSmall from "@/assets/BGSmall.svg";

export const HowItWorks = () => {
  const steps = [
    {
      title: "Your investments deserve more than returns",
      description: "KaratClub transforms your portfolio into access. Exclusive rewards, elevated living, zero compromise on control."
    },
    {
      title: "We turn your investments into access",
      description: "KaratClub uses your portfolio strength to unlock privileges, while your investments remain fully yours."
    },
    {
      title: "Stay Invested <br /> Upgrade your life",
      description: "Exclusive perks, premium access, and lifestyle rewards. All driven by your portfolio."
    },
  ];

  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-scroll for mobile only
  useEffect(() => {
    if (isMobile && containerRef.current) {
      const startAutoScroll = () => {
        autoScrollRef.current = setInterval(() => {
          setCurrentIndex((prevIndex) => {
            const nextIndex = prevIndex === steps.length - 1 ? 0 : prevIndex + 1;

            // Scroll to the next box
            if (containerRef.current) {
              const container = containerRef.current;
              const cardWidth = container.querySelector('div')?.offsetWidth || 280;
              const gap = 16;
              container.scrollTo({
                left: nextIndex * (cardWidth + gap),
                behavior: 'smooth'
              });
            }

            return nextIndex;
          });
        }, 3000); // Change every 3 seconds
      };

      startAutoScroll();

      return () => {
        if (autoScrollRef.current) {
          clearInterval(autoScrollRef.current);
        }
      };
    }
  }, [isMobile, steps.length]);

  const handleScroll = () => {
    if (!containerRef.current || !isMobile) return;
    const container = containerRef.current;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.querySelector('div')?.offsetWidth || 280;
    const gap = 16;
    const newIndex = Math.round(scrollLeft / (cardWidth + gap));

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < steps.length) {
      setCurrentIndex(newIndex);
    }
  };

  return (
    <section id="how_it_works" className="p-0 md:p-0 lg:p-0 rounded-3xl mt-0 md:mt-0 sm:mt-0">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-8">
        <h2 className="text-4xl md:text-6xl font-bold mb-4 md:mb-0 md:w-9/12">
          <span className="block md:hidden">How KaratClub Works</span>
          <span className="hidden md:block">How KaratClub<br />Works</span>
          <div className="bg-[#DBAD71] w-[200px] h-[1px] rounded-[19px] opacity-100 md:h-[4px]"></div>
        </h2>
      </div>

      {/* Content Container */}
      <div className="rounded-2xl p-0 md:p-0">
        {!isMobile ? (
          // Desktop - All 3 boxes side by side (static)
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                style={{
                  backgroundImage: `url(${MembershipFDBG})`,
                  backgroundSize: 'cover',
                  // backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '12px',
                  border: '0.77px solid',
                  borderImage: 'radial-gradient(33.3% 25.84% at 61.34% 0%, #E8B339 0%, rgba(232, 179, 57, 0) 100%) 1'
                }}
                className="rounded-xl p-6 md:p-10  min-h-[300px] flex flex-col justify-center"
              >
                <h3
                  className="font-medium text-4xl md:text-4xl text-[#F7F7F7] mb-4"
                  dangerouslySetInnerHTML={{ __html: step.title }}
                />
                <p
                  className="font-regular text-lg text-[#FFFFFF99]"
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
              </div>
            ))}
          </div>
        ) : (
          // Mobile - Horizontal scroll with auto-scroll (no arrows, no navigation)
          <div
            ref={containerRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4"
            onScroll={handleScroll}
          >
            {steps.map((step, index) => (
              <div
                key={index}
                style={{
                  backgroundImage: `url(${MembershipFDBG})`,
                  backgroundSize: 'cover',
                  // backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '12px',
                  border: '0.77px solid',
                  borderImage: 'radial-gradient(33.3% 25.84% at 61.34% 0%, #E8B339 0%, rgba(232, 179, 57, 0) 100%) 1'
                }}
                className="snap-start flex-shrink-0 w-[calc(100vw-80px)] rounded-xl p-6  min-h-[300px] flex flex-col justify-center"
              >
                <h3
                  className="font-medium text-3xl text-[#F7F7F7] mb-4"
                  dangerouslySetInnerHTML={{ __html: step.title }}
                />
                <p
                  className="font-regular text-base text-[#FFFFFF99]"
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};